"use client";

import type * as React from "react";

import { Blocks, ContentCard } from "@/types/cms";
import SectionHeader from "@/components/sections/utils/SectionHeader";
import { Separator } from "@/components/separator/separator";
import { getSectionSpacingClasses, normalizeSpace } from "@/components/sections/utils/spacing";
import GiftForm from "./GiftForm";
import styles from "./GiftFormSection.module.css";

/**
 * Gift Product Section (`section.gift-form`) — the /gift PDP. Header +
 * value props + the selectable price cards with an "Add to cart" CTA that
 * stores the choice in the gift-cart cookie and navigates to /cart (the
 * Cart Section block owns the purchase form there). Header, alignment,
 * width, spacing, and divider behave like a card section's; the header and
 * options row bleed past the section width on desktop.
 */

interface GiftFormSectionData {
  title?: string | null;
  eyebrow?: string | null;
  icon?: string | null;
  content?: Blocks;
  content_cards?: ContentCard[];
  valueProps?: Blocks;
  ctaLabel?: string | null;
  oneTimeNote?: string | null;
  textJustify?: "left" | "center" | "right" | null;
  contentAlign?: "left" | "center" | "right" | null;
  headerWidth?: "sm" | "md" | "lg" | null;
  divider?: boolean | null;
  sectionWidth?: string | null;
  spaceTop?: string | null;
  spaceBottom?: string | null;
}

interface GiftFormSectionProps {
  data: GiftFormSectionData;
}

/** Everything in this section's bled parts renders 128px wider than the
 *  section's own content width (64px past each edge). The `100%` resolves
 *  against the section container wherever the var is used. */
const BLEED_WIDTH = "calc(100% + 128px)";

const GiftFormSection = ({ data }: GiftFormSectionProps) => {
  if (!data) return null;

  const showDivider = data.divider === true;
  // Divider-above-space: same idiom as CardSection — with a divider the
  // section drops its mt-* and spaceTop becomes the gap below the divider.
  const spaceTopToken = normalizeSpace(data.spaceTop ?? undefined);
  const spaceBottomToken = normalizeSpace(data.spaceBottom ?? undefined);
  const sectionSpacingClass = showDivider
    ? (spaceBottomToken ? `mb-${spaceBottomToken}` : "")
    : getSectionSpacingClasses({
        spaceTop: data.spaceTop ?? undefined,
        spaceBottom: data.spaceBottom ?? undefined,
      });
  const dividerGapClass = spaceTopToken
    ? (styles as Record<string, string>)[
        `dividerGap${spaceTopToken[0].toUpperCase()}${spaceTopToken.slice(1)}`
      ]
    : "";
  const dividerClass = [styles.divider, dividerGapClass].filter(Boolean).join(" ");

  const widthClass = data.sectionWidth ? `w-${data.sectionWidth}` : undefined;
  const sectionClass = ["section", sectionSpacingClass].filter(Boolean).join(" ");
  const containerClass = ["section-container", widthClass].filter(Boolean).join(" ");

  // Cards render in the order they're attached in the admin.
  return (
    <section
      id="giftform"
      className={sectionClass}
      style={{ "--gift-bleed-width": BLEED_WIDTH } as React.CSSProperties}
    >
      <div className={containerClass}>
        {showDivider && (
          <div className={dividerClass}>
            <Separator />
          </div>
        )}
        {/* Bleeds with the options row below it (see .bleed) so the header's
            edges line up with the value props / cards. */}
        <div className={styles.bleed}>
          <SectionHeader
            content={data.content}
            align={data.contentAlign ?? undefined}
            textAlign={data.textJustify ?? undefined}
            headerWidth={data.headerWidth ?? undefined}
            eyebrow={data.eyebrow ?? undefined}
            icon={data.icon ?? undefined}
          />
        </div>
        <div className="reveal-item" style={{ "--reveal-index": 1 } as React.CSSProperties}>
          <GiftForm
            cards={data.content_cards ?? []}
            ctaLabel={data.ctaLabel}
            oneTimeNote={data.oneTimeNote}
            valueProps={data.valueProps}
          />
        </div>
      </div>
    </section>
  );
};

export default GiftFormSection;
